import React, { useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-balham.css';
import useGridOptionsManageApproval from '../../../hooks/grid/gridOptions/useGridOptionsManageApproval';
import commonColDef from '../../../data/grid/commonColDef';
import useColdefManageApproval from '../../../hooks/grid/coldef/useColdefManageApproval';
import OrganizationIdCellRenderer from '../../cellRenderers/OrganizationIdCellRenderer';
import SteelMakerCellRenderer from '../../cellRenderers/SteelMakerCellRenderer';
import BusinessTypeCellRenderer from '../../cellRenderers/BusinessTypeCellRenderer';
import SteelCategoryCellRenderer from '../../cellRenderers/SteelCategoryCellRenderer';
import ConditionStatusRenderer from '../../cellRenderers/ConditionStatusRenderer';
import ApproverEmailCellRenderer from '../../cellRenderers/ApproverEmailCellRenderer';
import { useTranslation } from 'react-i18next';
import useLocales from '../../../hooks/useLocales';
import { useDispatch } from '../../../redux/store';
import { openDialog, getConfirmCondition } from '../../../redux/slices/approval';
import type { ColDef, ColumnApi, GridApi, GridReadyEvent, RowDoubleClickedEvent } from 'ag-grid-community';
import type { ConditionDetailType } from '../../../types/condition';
import type { ApproveType } from '../../../types/approval';
import type { OptionsType } from '../../../types/options';
import type { BusinessType, SteelCategory, SteelMaker } from '../../../types/common';
import type { AdminPowerUsers } from '../../../types/user';

interface ConditionListGridProps {
  isLoading: boolean;
  confirmList: ApproveType[] | undefined;
  steelMaker: OptionsType<SteelMaker>[];
  businessType: OptionsType<BusinessType>[];
  steelCategory: OptionsType<SteelCategory>[];
  adminPowerUsers: AdminPowerUsers | undefined;
}

const ConfirmListGrid = ({ isLoading, confirmList, steelMaker, businessType, steelCategory, adminPowerUsers }: ConditionListGridProps): React.ReactElement => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { currentLang } = useLocales();
  const coldefManageApproval = useColdefManageApproval(t);
  const [gridApi, setGridApi] = useState<GridApi | undefined>(undefined);
  const [gridColumnApi, setGridColumnApi] = useState<ColumnApi | undefined>(undefined);
  const { gridOptions } = useGridOptionsManageApproval();
  const customColDef: ColDef = {
    cellClass: 'cell',
    flex: 1,
  };
  const defaultColDef: ColDef = Object.assign({}, commonColDef, customColDef);
  const columnDefs: ColDef[] = coldefManageApproval;
  const frameworkComponents = {
    organizationIdCellRenderer: OrganizationIdCellRenderer,
    steelMakerCellRenderer: SteelMakerCellRenderer,
    businessTypeCellRenderer: BusinessTypeCellRenderer,
    steelCategoryCellRenderer: SteelCategoryCellRenderer,
    conditionStatusRenderer: ConditionStatusRenderer,
    approverEmailCellRenderer: ApproverEmailCellRenderer,
  };

  // SIDE EFFECT
  // ================================================================================

  // 確認用
  // useEffect(() => {
  // }, []);

  /**
   * Loading, NoRowsのグリッド表示
   */
  useEffect(() => {
    if (gridApi) {
      if (isLoading) {
        gridApi.showLoadingOverlay();
      } else {
        if (confirmList?.length) {
          gridApi.hideOverlay();
        } else {
          gridApi.showNoRowsOverlay();
        }
      }
    } else {
      // do nothing
    }
  }, [gridApi, isLoading, confirmList]);

  // EVENT HANDLER
  // ================================================================================

  /**
   * グリッドが準備できたらAPIを利用できるようにする
   * @param params GridReadyEvent
   */
  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  /**
   * ダブルクリックで編集モードにする
   * @param params GridReadyEvent
   */
  const onRowDoubleClicked = (event: RowDoubleClickedEvent) => {
    // console.log(event);
    const data: ConditionDetailType = event.data;
    dispatch(getConfirmCondition(data.id));
    dispatch(openDialog());
  };

  // VIEW
  // ================================================================================

  return (
    <>
      <div className="ag-theme-balham" style={{ height: '75vh' }}>
        <AgGridReact
          immutableData={true}
          gridOptions={gridOptions}
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          rowData={confirmList ?? []}
          onGridReady={onGridReady}
          onRowDoubleClicked={onRowDoubleClicked}
          frameworkComponents={frameworkComponents}
          // filterValueGetterで使うデータ
          context={{
            currentLang,
            // reduxにあるものはコピーを渡す
            steelMaker: steelMaker?.map((maker) => structuredClone(maker)),
            businessType: businessType?.map((business) => structuredClone(business)),
            steelCategory: steelCategory?.map((category) => structuredClone(category)),
            adminPowerUsers: adminPowerUsers ? structuredClone(adminPowerUsers) : undefined,
          }}
        />
      </div>
    </>
  );
};

export default ConfirmListGrid;
